import queryString from "query-string";
import type { ParsedQuery } from "query-string";

/** Options used by list views that sync filters to the URL (e.g. Payments). */
export const PAYMENTS_QUERY_OPTS = {
  parse: {
    arrayFormat: "separator" as const,
    arrayFormatSeparator: "|",
  },
  stringify: {
    arrayFormat: "separator" as const,
    arrayFormatSeparator: "|",
    skipEmptyString: true,
    skipNull: true,
    sort: (a: string, b: string) => a.localeCompare(b),
  },
};

/**
 * Parse + stringify so equivalent queries compare equal (stable key order and formatting).
 */
export function normalizeSearchString(
  search: string | undefined,
  opts: typeof PAYMENTS_QUERY_OPTS = PAYMENTS_QUERY_OPTS
): string {
  const raw =
    !search || search === "?"
      ? ""
      : search.startsWith("?")
        ? search.slice(1)
        : search;
  if (!raw) return "";
  const parsed = queryString.parse(raw, opts.parse);
  const qs = queryString.stringify(parsed as Record<string, unknown>, opts.stringify);
  return qs ? `?${qs}` : "";
}

export function searchesEquivalent(
  a: string | undefined,
  b: string | undefined,
  opts: typeof PAYMENTS_QUERY_OPTS = PAYMENTS_QUERY_OPTS
): boolean {
  return normalizeSearchString(a, opts) === normalizeSearchString(b, opts);
}

/** Parsed payments list query: every value is a string[] for stable merging with updateUrl. */
export function parsePaymentsSearchToRecord(
  search: string | undefined
): Record<string, string[]> {
  const raw =
    !search || search === "?"
      ? ""
      : search.startsWith("?")
        ? search.slice(1)
        : search;
  const parsed = queryString.parse(raw, PAYMENTS_QUERY_OPTS.parse);
  const out: Record<string, string[]> = {};
  Object.entries(parsed).forEach(([key, value]) => {
    if (typeof value === "string") {
      if (value !== "") out[key] = [value];
      return;
    }
    if (Array.isArray(value)) {
      const cleaned = value.map(String).filter((v) => v !== "");
      if (cleaned.length) out[key] = cleaned;
    }
  });
  return out;
}

/**
 * Flatten query-string parse output to plain string values for axios `params`.
 * Arrays (e.g. from bracket or repeated keys) become a single string so the server
 * sees `balanceMin=100` instead of `balanceMin[]=100`, which some routes ignore.
 */
export function flattenParsedQueryForApi(
  parsed: ParsedQuery<string>
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      const first = value.find((x) => x != null && String(x).trim() !== "");
      if (first !== undefined) out[key] = String(first);
    } else {
      const s = String(value);
      if (s !== "") out[key] = s;
    }
  }
  return out;
}

/**
 * Params for `GET /api/v2/company/payments/payment-list`.
 * Browser URL stays camelCase (`balanceMin` / `balanceMax`). Some server handlers only
 * read snake_case for balance; sending both avoids silent no-ops without breaking
 * handlers that already use camelCase.
 */
export function buildPaymentListRequestParams(
  parsed: ParsedQuery<string>
): Record<string, string> {
  const flat = flattenParsedQueryForApi(parsed);
  const out = { ...flat };
  // Some server handlers read snake_case only.
  if (out.balanceMin !== undefined) {
    out.balance_min = out.balanceMin;
  }
  if (out.balanceMax !== undefined) {
    out.balance_max = out.balanceMax;
  }
  if (out.amountMin !== undefined) {
    out.amount_min = out.amountMin;
  }
  if (out.amountMax !== undefined) {
    out.amount_max = out.amountMax;
  }

  // If the user enters a numeric search term, treat it as a payment id lookup too.
  // This makes "search by ID" work even when the handler doesn't include payment_id
  // in its generic `search` logic.
  if (typeof out.search === "string") {
    const s = out.search.trim();
    if (/^\d+$/.test(s)) {
      out.payment_id = s;
      out.id = s;
    }
  }
  return out;
}
